import { BLOCKMETA_CONFIRMED_SUBJECT } from './constants.js';

export type BlockmetaValidationReason =
  | 'bad_subject'
  | 'bad_json'
  | 'bad_blockhash'
  | 'bad_timestamp'
  | 'regressing_block_meta';

/**
 * `regressing_block_meta` is expected out-of-order delivery on core NATS (the
 * store already keeps the last good value) — not a malformed payload. Callers
 * use this to avoid logging it at warn/error alongside genuinely bad payloads.
 */
export function isExpectedBlockmetaRejection(reason: BlockmetaValidationReason): boolean {
  return reason === 'regressing_block_meta';
}

export interface BlockmetaLatestV0 {
  readonly timestamp: number;
  readonly blockhash: string;
}

export interface AcceptedBlockmetaSnapshot extends BlockmetaLatestV0 {
  readonly receivedAtMonoMs: number;
}

export type ParseBlockmetaPayloadResult =
  | { readonly ok: true; readonly snapshot: AcceptedBlockmetaSnapshot }
  | { readonly ok: false; readonly reason: BlockmetaValidationReason };

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE58_DIGITS: ReadonlyMap<string, number> = new Map(
  [...BASE58_ALPHABET].map((char, index) => [char, index]),
);

export function parseBlockmetaPayload(
  subject: string,
  payload: Uint8Array,
  receivedAtMonoMs: number,
): ParseBlockmetaPayloadResult {
  if (subject !== BLOCKMETA_CONFIRMED_SUBJECT) {
    return { ok: false, reason: 'bad_subject' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return { ok: false, reason: 'bad_json' };
  }

  if (!isRecord(parsed)) {
    return { ok: false, reason: 'bad_json' };
  }
  if (!isPositiveSafeInteger(parsed.timestamp)) {
    return { ok: false, reason: 'bad_timestamp' };
  }
  if (!isValidBlockhash(parsed.blockhash)) {
    return { ok: false, reason: 'bad_blockhash' };
  }

  return {
    ok: true,
    snapshot: {
      timestamp: parsed.timestamp,
      blockhash: parsed.blockhash,
      receivedAtMonoMs,
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isValidBlockhash(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  const decoded = decodeBase58(value);
  return decoded !== null && decoded.length === 32;
}

function decodeBase58(value: string): Uint8Array | null {
  const bytes: number[] = [0];
  for (const char of value) {
    const digit = BASE58_DIGITS.get(char);
    if (digit === undefined) return null;

    let carry = digit;
    for (let index = 0; index < bytes.length; index += 1) {
      const current = bytes[index];
      if (current === undefined) return null;
      carry += current * 58;
      bytes[index] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  let leadingZeroes = 0;
  for (const char of value) {
    if (char !== '1') break;
    leadingZeroes += 1;
  }

  const decoded = new Uint8Array(leadingZeroes + bytes.length);
  for (let index = 0; index < leadingZeroes; index += 1) {
    decoded[index] = 0;
  }
  for (let index = 0; index < bytes.length; index += 1) {
    const current = bytes[index];
    if (current === undefined) return null;
    decoded[decoded.length - 1 - index] = current;
  }
  return decoded;
}
